export type Tier = "entry" | "pro" | "premium" | "team" | "business";

export const TRIAL_DAYS = 7;

export const PRO_PRICE_ID = "pro_monthly";
export const PREMIUM_PRICE_ID = "premium_monthly";
export const TEAM_PRICE_ID = "team_monthly";
export const BUSINESS_PRICE_ID = "business_monthly";

export const TIER_BY_PRICE_ID: Record<string, Tier> = {
  [PRO_PRICE_ID]: "pro",
  [PREMIUM_PRICE_ID]: "premium",
  [TEAM_PRICE_ID]: "team",
  [BUSINESS_PRICE_ID]: "business",
};

export const TIER_LABEL: Record<Tier, string> = {
  entry: "Entry",
  pro: "Pro",
  premium: "Premium",
  team: "Team",
  business: "Business",
};

export const TIER_PRICE_LABEL: Record<Tier, string> = {
  entry: "Free",
  pro: "£3.99/month",
  premium: "£9.99/month",
  team: "£7.99 per user/month",
  business: "£14.99 per user/month",
};

/** Monthly pence per seat, for seat-based plans only. */
export const SEAT_PENCE: Record<"team" | "business", number> = {
  team: 799,
  business: 1499,
};

/** Smallest seat count we sell each seat-based plan at. */
export const MIN_SEATS: Record<"team" | "business", number> = {
  team: 2,
  business: 5,
};

export const TIER_RANK: Record<Tier, number> = {
  entry: 0,
  pro: 1,
  premium: 2,
  team: 3,
  business: 4,
};

export function formatGbp(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

/** "12 x £7.99 = £95.88/month" style total for a seat-based plan. */
export function seatTotalLabel(plan: "team" | "business", seats: number): string {
  return `${formatGbp(SEAT_PENCE[plan] * Math.max(1, seats))}/month`;
}

export type SubscriptionRow = {
  tier: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end?: boolean | null;
  seats?: number | null;
} | null;

const ACTIVE_STATUSES = ["active", "trialing", "past_due", "manual"];

export function isSubscriptionActive(row: SubscriptionRow): boolean {
  if (!row) return false;
  const status = row.status ?? "";
  const end = row.current_period_end ? new Date(row.current_period_end).getTime() : null;
  if (status === "manual") return true;
  if (ACTIVE_STATUSES.includes(status)) return end === null || end > Date.now();
  if (status === "canceled") return end !== null && end > Date.now();
  return false;
}

export function subscriptionTier(row: SubscriptionRow): Tier {
  if (!isSubscriptionActive(row)) return "entry";
  const tier = row?.tier ?? "";
  return tier in TIER_RANK && tier !== "entry" ? (tier as Tier) : "entry";
}

export function trialDaysRemaining(startedAt: string | null | undefined): number {
  if (!startedAt) return TRIAL_DAYS;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return TRIAL_DAYS;
  const elapsedDays = (Date.now() - started) / 86_400_000;
  return Math.max(0, Math.ceil(TRIAL_DAYS - elapsedDays));
}

export type Entitlements = {
  tier: Tier;
  hasPro: boolean;
  hasPremium: boolean;
  hasTeam: boolean;
  hasBusiness: boolean;
  seats: number;
  trialStartedAt: string | null;
  trialActive: boolean;
  trialDaysLeft: number;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  billingEmail: string | null;
  /** Where the tier came from, so the interface can explain it. */
  tierSource: TierSource;
  workspace: WorkspaceEntitlement | null;
};

/**
 * A seat-based plan reaching this person through a workspace they belong to.
 * The workspace owner pays; everyone holding a seat inherits the tier.
 */
export type WorkspaceEntitlement = {
  organisationId: string;
  organisationName: string;
  tier: Tier;
  status: string | null;
  currentPeriodEnd: string | null;
  /** Seats the plan pays for. */
  seats: number;
  /** People currently in the workspace, whether or not they hold a seat. */
  seatsUsed: number;
  /** This person's position in the seat queue: owner first, then join order. */
  seatNumber: number;
  hasSeat: boolean;
};

export type TierSource = "none" | "trial" | "personal" | "workspace";

/** The better of two tiers. */
export function higherTier(a: Tier, b: Tier): Tier {
  return TIER_RANK[a] >= TIER_RANK[b] ? a : b;
}

export function buildEntitlements(
  row: (SubscriptionRow & { billing_email?: string | null }) | null,
  trialStartedAt: string | null,
  workspace: WorkspaceEntitlement | null = null,
): Entitlements {
  const personalTier = subscriptionTier(row);
  // A workspace seat only grants anything while the seat is actually held: past
  // the seat count the plan pays for, a member falls back to their own plan.
  const workspaceTier = workspace?.hasSeat ? workspace.tier : "entry";
  const tier = higherTier(personalTier, workspaceTier);
  const rank = TIER_RANK[tier];
  const paidPro = rank >= TIER_RANK.pro;
  const trialDaysLeft = trialDaysRemaining(trialStartedAt);
  const trialActive = !paidPro && trialDaysLeft > 0;

  const tierSource: TierSource = !paidPro
    ? trialActive
      ? "trial"
      : "none"
    : TIER_RANK[workspaceTier] > TIER_RANK[personalTier]
      ? "workspace"
      : "personal";

  return {
    tier,
    hasPro: paidPro || trialActive,
    hasPremium: rank >= TIER_RANK.premium,
    hasTeam: rank >= TIER_RANK.team,
    hasBusiness: rank >= TIER_RANK.business,
    seats:
      tierSource === "workspace" ? Math.max(1, workspace?.seats ?? 1) : Math.max(1, row?.seats ?? 1),
    trialStartedAt: trialStartedAt ?? null,
    trialActive,
    trialDaysLeft,
    status: tierSource === "workspace" ? (workspace?.status ?? null) : (row?.status ?? null),
    currentPeriodEnd:
      tierSource === "workspace"
        ? (workspace?.currentPeriodEnd ?? null)
        : (row?.current_period_end ?? null),
    cancelAtPeriodEnd: Boolean(row?.cancel_at_period_end),
    billingEmail: row?.billing_email ?? null,
    tierSource,
    workspace: workspace ?? null,
  };
}

